import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminUsersTab from './AdminUsersTab'
import AdminSkillsTab from './AdminSkillsTab'
import AdminInsightsTab from './AdminInsightsTab'
import AdminSecurityTab from './AdminSecurityTab'
import { getTokenPayload } from '../api/security'
import styles from './Admin.module.css'

const ADMIN_TABS = [
  { key: 'users', label: 'User Management' },
  { key: 'skills', label: 'Skills Settings' },
  { key: 'insights', label: 'Insight Selection' },
]
const SECURITY_TAB = { key: 'security', label: 'Security' }

/**
 * Admin — the Administration tab: workspace user management, skill-pipeline
 * sensitivity settings, Control Panel insight selection, and tenant-wide
 * Security settings.
 *
 * Users tab talks to gaussiq-identity (src/api/security.js); Skills/Insights
 * tabs talk to gaussiq-admin (src/api/admin.js).
 *
 * Sidebar already hides the link to here for non-admins; this is the matching
 * guard for anyone who reaches /admin directly by URL (same redirect-away
 * pattern AppLayout uses for unauthenticated users) — every API call this page
 * makes is 403'd server-side for a non-admin anyway, so this just avoids
 * rendering a tab full of failed requests instead of leaving a real hole.
 * Security is visible to any admin (not superadmin-only) — the backend's
 * GET/PATCH /api/security/tenant/mfa accepts admin_right too, so this isn't
 * just a widened button.
 */
export default function Admin() {
  const [tab, setTab] = useState('users')
  const navigate = useNavigate()
  const rights = getTokenPayload()?.rights
  const isSuperAdmin = Boolean(rights?.superadmin_right)
  // superadmin_right overrides every granular right, admin_right included.
  const isAdmin = Boolean(rights?.admin_right) || isSuperAdmin
  const tabs = isAdmin ? [...ADMIN_TABS, SECURITY_TAB] : ADMIN_TABS

  useEffect(() => {
    if (!isAdmin) navigate('/dashboard', { replace: true })
  }, [isAdmin, navigate])

  if (!isAdmin) return null

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Administration</h1>
        <p className={styles.pageSub}>Manage who has access to the workspace and what they can do.</p>
      </div>

      <div className={styles.tabs} role="tablist">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={styles.tabPanel}>
        {tab === 'users' && <AdminUsersTab />}
        {tab === 'skills' && <AdminSkillsTab />}
        {tab === 'insights' && <AdminInsightsTab isSuperAdmin={isSuperAdmin} />}
        {tab === 'security' && isAdmin && <AdminSecurityTab />}
      </div>
    </div>
  )
}
